import type { FormConfirmationRequest } from '@repo/hooks/useZodForm';

/** The numbers a switch change affects. */
export interface AffectedNumbers {
  /** Exact count, or null when only "more than one" is known */
  count: number | null;
  /** The numbers loaded for the list (may be fewer than `count`) */
  numbers: string[];
}

/** A short scrollable list of the affected numbers, for the confirmation dialog. */
const AffectedNumbersList = ({ count, numbers }: AffectedNumbers) => {
  if (!numbers.length) return null;
  const more = count !== null ? count - numbers.length : 0;

  return (
    <div className="flex flex-col gap-2 w-full">
      <span className="text-sm font-bold">Numbers using this switch</span>
      <ul
        aria-label="Affected numbers"
        className="overflow-y-auto px-3 py-2 max-h-40 text-sm rounded-md border divide-y border-grey-400 divide-grey-400/50 dark:border-grey-700 dark:divide-grey-700"
      >
        {numbers.map((number) => (
          <li key={number} className="py-1.5">
            {number}
          </li>
        ))}
      </ul>
      {more > 0 && (
        <span className="text-xs text-grey-600 dark:text-grey-400">…and {more} more</span>
      )}
    </div>
  );
};

/**
 * The confirmation shown before saving a switch that other numbers share, or
 * null when only one number (or none) uses it.
 */
export const sharedSwitchConfirmation = (
  affected: AffectedNumbers | undefined,
  change: 'forwarding' | 'details'
): FormConfirmationRequest | null => {
  if (!affected) return null;
  const numbersText = affected.count !== null ? `${affected.count} numbers` : 'multiple numbers';
  const content = <AffectedNumbersList {...affected} />;

  return change === 'forwarding'
    ? {
        title: `Update forwarding for ${numbersText}?`,
        description: `This switch is used by ${numbersText}. Saving changes how messages are forwarded for all of them.`,
        confirmText:
          affected.count !== null ? `Update ${affected.count} numbers` : 'Update all numbers',
        content,
      }
    : {
        title: `Update this switch for ${numbersText}?`,
        description: `This switch is used by ${numbersText}. Its name and notes change for all of them.`,
        confirmText: 'Save changes',
        content,
      };
};
